<template>
  <div class="ma-4 px-4">
    <!-- Filters Row -->
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-3">
        <v-text-field
          v-model="form.current_term_no"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Current Term"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0" v-if="activeTab == 2">
        <v-autocomplete
          v-model="form.exam_batch"
          :items="examResultBatches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Exam Batch"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="4" class="py-0 my-0">
        <v-btn
          class="accent white--text"
          :disabled="loading"
          :loading="loading"
          @click="searchFilter"
        >
          Search
        </v-btn>
        <v-btn
          class="accent white--text ml-2"
          :disabled="!students.length"
          :loading="loading"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tabs + Action Button -->
    <v-row class="d-flex align-center">
      <v-col cols="10" class="d-flex justify-center">
        <v-tabs
          v-model="activeTab"
          @change="onTabChange"
          grow
          background-color="transparent"
        >
          <v-tab v-for="mode in modes" :key="mode.value">
            <v-icon small left>{{ mode.icon }}</v-icon>
            {{ mode.label }}
          </v-tab>
        </v-tabs>
      </v-col>
      <v-col cols="2" class="d-flex justify-end">
        <v-btn
          :disabled="!selectedStudents.length || loading"
          :loading="actionLoading"
          class="success white--text mr-2"
          @click="confirmAction"
        >
          {{ modeConfig.actionLabel }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Tab Content -->
    <v-tabs-items v-model="activeTab" class="mt-4">
      <v-tab-item v-for="mode in modes" :key="mode.value">
        <v-row>
          <v-col cols="12">
            <v-sheet outlined>
              <v-data-table
                v-model="selectedStudents"
                :headers="currentHeaders"
                :items="students"
                :loading="loading"
                fixed-header
                show-select
                :show-select-all="true"
                multi-sort
                class="secondary row-height-50"
                item-key="usn_id"
              >
                <!-- Current Term -->
                <template #[`item.current_term_no`]="{ item }">
                  <div
                    class="d-flex align-center"
                    v-if="mode.value === 'promotion'"
                  >
                    <span>Term {{ item.current_term_no }}</span>
                    <v-icon x-small color="success" class="mx-1"
                      >mdi-arrow-right</v-icon
                    >
                    <span class="success--text font-weight-bold"
                      >Term {{ item.current_term_no + 1 }}</span
                    >
                  </div>
                  <div v-else>
                    {{ item.current_term_no }}
                  </div>
                </template>

                <!-- CGPA -->
                <template #[`item.cgpa`]="{ item }">
                  <span
                    v-if="item.cgpa"
                    class="font-weight-bold"
                    :class="cgpaColor(item.cgpa)"
                    >{{ item.cgpa }}</span
                  >
                  <span v-else class="grey--text">—</span>
                </template>

                <!-- Remaining Terms (terminate) -->
                <template #[`item.remaining_terms`]="{ item }">
                  <span
                    class="font-weight-bold"
                    :class="
                      item.remaining_terms <= 0
                        ? 'error--text'
                        : item.remaining_terms <= 2
                        ? 'orange--text'
                        : 'success--text'
                    "
                    >{{ item.remaining_terms }}</span
                  >
                </template>

                <!-- Backlog Papers (terminate) -->
                <template #[`item.backlog_papers`]="{ item }">
                  <v-tooltip
                    v-for="paper in item.backlog_papers"
                    :key="paper.course_code"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <v-chip
                        x-small
                        outlined
                        color="error"
                        class="mr-1 my-1"
                        v-bind="attrs"
                        v-on="on"
                        >{{ paper.course_code }}</v-chip
                      >
                    </template>
                    <span
                      >{{ paper.course_desc }} — Term {{ paper.term_no }}, last
                      attempt {{ paper.last_attempt }}</span
                    >
                  </v-tooltip>
                </template>

                <!-- Loading -->
                <template #progress>
                  <v-progress-linear indeterminate :color="modeConfig.color" />
                </template>

                <!-- No data -->
                <template #no-data>
                  <div class="no-data-state py-10">
                    <v-icon size="48" color="grey lighten-2"
                      >mdi-account-search</v-icon
                    >
                    <div class="no-data-text mt-2">No students found</div>
                    <div class="no-data-sub">
                      Apply filters and click Search
                    </div>
                  </div>
                </template>
              </v-data-table>
            </v-sheet>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>

    <!-- Confirm Dialog -->
    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card class="confirm-dialog">
        <div
          class="confirm-dialog__header"
          :class="`confirm-dialog__header--${activeMode}`"
        >
          <v-icon color="white" size="32" class="mb-2">{{
            modeConfig.actionIcon
          }}</v-icon>
          <div class="confirm-dialog__title">
            Confirm {{ modeConfig.actionLabel }}
          </div>
          <div class="confirm-dialog__sub">This action cannot be undone</div>
        </div>
        <v-card-text class="pt-4">
          <div class="confirm-info mb-3">
            You are about to update
            <strong>{{ selectedStudents.length }}</strong> student(s):
          </div>

          <v-list dense class="confirm-list">
            <v-list-item
              v-for="s in showAllStudents
                ? selectedStudents
                : selectedStudents.slice(0, 5)"
              :key="s.usn_id"
              class="px-0"
            >
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small color="indigo">mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="body-2">{{
                  s.student_name
                }}</v-list-item-title>
                <v-list-item-subtitle class="caption">{{
                  s.usn_id
                }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
            <v-list-item
              v-if="selectedStudents.length > 5 && !showAllStudents"
              class="px-0"
              @click="showAllStudents = true"
              style="cursor: pointer"
            >
              <v-list-item-content>
                <v-list-item-subtitle class="caption primary--text">
                  + {{ selectedStudents.length - 5 }} more students (click to
                  view all)
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <v-alert
            v-if="activeMode === 'promotion'"
            color="info"
            outlined
            dense
            class="mt-3 mb-0"
            icon="mdi-information"
          >
            Students will be moved from their current term to the next term.
          </v-alert>
          <v-alert
            v-else-if="activeMode === 'program_completed'"
            color="teal"
            outlined
            dense
            class="mt-3 mb-0"
            icon="mdi-school"
          >
            Students will be marked as <strong>Program Completed</strong>.
          </v-alert>
          <v-alert
            v-else-if="activeMode === 'graduated'"
            color="purple"
            outlined
            dense
            class="mt-3 mb-0"
            icon="mdi-medal"
          >
            Students will be marked as <strong>Graduated</strong>. Exam results
            verified.
          </v-alert>
          <v-alert
            v-else-if="activeMode === 'inactive'"
            color="orange darken-2"
            outlined
            dense
            class="mt-3 mb-0"
            icon="mdi-account-off"
          >
            Students will be marked as <strong>Inactive</strong>. They will no
            longer appear in active student lists.
          </v-alert>
          <v-alert
            v-else-if="activeMode === 'terminate'"
            color="red darken-2"
            outlined
            dense
            class="mt-3 mb-0"
            icon="mdi-account-cancel"
          >
            Students will be marked as <strong>Terminated</strong> for not
            clearing all papers within the maximum duration.
            <div
              v-if="selectedStudents.some((s) => s.remaining_terms > 0)"
              class="mt-1 font-weight-bold"
            >
              {{
                selectedStudents.filter((s) => s.remaining_terms > 0).length
              }}
              selected student(s) still have terms left to clear backlogs.
            </div>
          </v-alert>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn
            outlined
            @click="(confirmDialog = false), (showAllStudents = false)"
            :disabled="actionLoading"
            >Cancel</v-btn
          >
          <v-spacer />
          <v-btn
            depressed
            dark
            :color="modeConfig.color"
            :loading="actionLoading"
            @click="executeAction"
          >
            <v-icon left small>{{ modeConfig.actionIcon }}</v-icon>
            Confirm {{ modeConfig.actionLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { studentProgressionHeaders } from "@/constants/headers";

export default {
  name: "StudentProgression",
  data() {
    return {
      activeTab: 0,
      form: {
        year_batch: null,
        exam_batch: null,
      },
      examResultBatches: [],
      batches: [],
      students: [],
      partners: [],
      programs: [],
      selectedStudents: [],
      search: "",
      loading: false,
      actionLoading: false,
      confirmDialog: false,
      showAllStudents: false,
      modes: [
        {
          value: "promotion",
          label: "Promotion",
          icon: "mdi-arrow-up-circle",
          color: "indigo",
        },
        {
          value: "program_completed",
          label: "Program Completed",
          icon: "mdi-school-outline",
          color: "teal",
        },
        {
          value: "graduated",
          label: "Graduated",
          icon: "mdi-medal-outline",
          color: "deep-purple",
        },
        {
          value: "inactive",
          label: "Inactive",
          icon: "mdi-account-off-outline",
          color: "orange darken-2",
        },
        {
          value: "terminate",
          label: "Potential Terminate",
          icon: "mdi-account-cancel-outline",
          color: "red darken-2",
        },
      ],
      modeConfigs: {
        promotion: {
          label: "Term Promotion",
          color: "indigo",
          icon: "mdi-arrow-up-circle-outline",
          actionIcon: "mdi-arrow-up-bold",
          actionLabel: "Promote",
          endpoint: "/promote_students",
        },
        program_completed: {
          label: "Program Completed",
          color: "teal",
          icon: "mdi-school-outline",
          actionIcon: "mdi-check-decagram",
          actionLabel: "Mark Completed",
          endpoint: "/mark_program_completed",
        },
        graduated: {
          label: "Graduation",
          color: "deep-purple",
          icon: "mdi-medal-outline",
          actionIcon: "mdi-medal",
          actionLabel: "Mark Graduated",
          endpoint: "/mark_graduated",
        },
        inactive: {
          label: "Inactive",
          color: "orange darken-2",
          icon: "mdi-account-off-outline",
          actionIcon: "mdi-account-off",
          actionLabel: "Mark Inactive",
          endpoint: "/mark_inactive",
        },
        terminate: {
          label: "Potential Terminate",
          color: "red darken-2",
          icon: "mdi-account-cancel-outline",
          actionIcon: "mdi-account-cancel",
          actionLabel: "Mark Terminated",
          endpoint: "/mark_terminated",
        },
      },
      studentProgressionHeaders,
    };
  },
  computed: {
    activeMode() {
      return this.modes[this.activeTab]?.value || "promotion";
    },
    modeConfig() {
      return this.modeConfigs[this.activeMode];
    },
    currentHeaders() {
      if (
        this.activeMode === "promotion" ||
        this.activeMode === "program_completed" ||
        this.activeMode === "inactive"
      )
        return studentProgressionHeaders;

      if (this.activeMode === "terminate")
        return [
          ...studentProgressionHeaders,
          ...[
            { text: "Status", value: "status" },
            { text: "Terms Elapsed", value: "elapsed_terms" },
            { text: "Max Terms", value: "max_terms" },
            { text: "Remaining", value: "remaining_terms" },
            // { text: "Backlogs", value: "backlog_count" },
            // { text: "Backlog Papers", value: "backlog_papers", sortable: false },
          ].map((h) => ({
            ...h,
            class: "black--text font-weight-bold secondary",
          })),
        ];

      return [
        ...studentProgressionHeaders,
        {
          text: "CGPA",
          value: "cgpa",
          class: "black--text font-weight-bold secondary",
        },
      ];
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Student Progression" }]);
    this.searchFilter();
    await Promise.all([
      this.generateBatchList(),
      this.generateExamBatchList(),
      this.getPrograms(),
      this.getPartners(),
    ]);
  },
  methods: {
    onTabChange() {
      this.students = [];
      this.selectedStudents = [];
      this.form = {};
      this.searchFilter();
    },
    cgpaColor(val) {
      if (val >= 8) return "success--text";
      if (val >= 6) return "orange--text";
      return "error--text";
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }
      if (obj.exam_batch && this.activeTab == 2) {
        const [year, batch] = obj.exam_batch.split("-");
        obj.exam_year = year;
        obj.exam_month = batch;
        delete obj.exam_batch;
      }
      return Object.keys(obj)
        .filter((k) => obj[k] !== undefined && obj[k] !== null && obj[k] !== "")
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
    },
    async searchFilter() {
      this.students = [];
      this.selectedStudents = [];
      this.loading = true;

      const params = this.objectToQueryParams({
        ...this.form,
        ...this.terminateParams(),
      });
      const endpointMap = {
        promotion: `/get_promotion_eligible?${params}`,
        program_completed: `/get_program_completion_eligible?${params}`,
        graduated: `/get_graduation_eligible?${params}`,
        inactive: `/get_inactive_students?${params}`,
        terminate: `/get_termination_candidates?${params}`,
      };

      try {
        const res = await this.$axios.get(endpointMap[this.activeMode]);
        this.students = res.data.data || [];
      } catch (error) {
        this.$store.commit("showSnackbar", {
          message: error?.response?.data?.message || "Failed to fetch students",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    terminateParams() {
      return this.activeMode === "terminate"
        ? { max_duration_reached: true }
        : {};
    },
    confirmAction() {
      if (!this.selectedStudents.length) return;
      this.confirmDialog = true;
    },
    async executeAction() {
      this.actionLoading = true;
      const usn_ids = this.selectedStudents.map((s) => s.usn_id);

      try {
        const res = await this.$axios.post(this.modeConfig.endpoint, {
          usn_ids,
        });
        this.confirmDialog = false;
        this.showAllStudents = false;

        this.$store.commit("showSnackbar", {
          message: `${this.modeConfig.actionLabel} completed for ${usn_ids.length} student(s)`,
          color: "success",
        });

        this.selectedStudents = [];
        this.searchFilter();
      } catch (error) {
        const errorMessage = await this.$store.dispatch(
          "throwError",
          error.response,
        );
        this.$store.commit("showSnackbar", {
          message: errorMessage,
          color: "error",
        });
      } finally {
        this.actionLoading = false;
      }
    },
    async generateBatchList() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data;
      } catch (e) {
        console.error(e);
      }
    },
    async generateExamBatchList() {
      try {
        const response = await this.$axios.get("/student_exam_session_batches");
        this.examResultBatches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPrograms() {
      try {
        const response = await this.$axios.get("/programs");
        this.programs = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async download() {
      this.loading = true;
      const config = this.modes[this.activeTab];
      try {
        this.$axios
          .get("/download_student_progression", {
            params: {
              ...this.form,
              ...this.terminateParams(),
              student_progression: config.value,
            },
            responseType: "blob",
          })
          .then((res) => {
            let blob = new Blob([res.data], { type: "text/csv" }),
              url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${config.label} Student.csv`);
            document.body.appendChild(link);
            link.click();
            this.loading = false;
          });
      } catch (e) {
        console.log(e);
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ─── No Data ───────────────────────────────── */
.no-data-state {
  text-align: center;
}
.no-data-text {
  font-size: 15px;
  font-weight: 600;
  color: #9e9e9e;
}
.no-data-sub {
  font-size: 12px;
  color: #bdbdbd;
  margin-top: 4px;
}

/* ─── Confirm Dialog ────────────────────────── */
.confirm-dialog {
  border-radius: 14px !important;
  overflow: hidden;
}
.confirm-dialog__header {
  padding: 28px 24px 20px;
  text-align: center;
}
.confirm-dialog__header--promotion {
  background: linear-gradient(135deg, #1a237e, #3949ab);
}
.confirm-dialog__header--program_completed {
  background: linear-gradient(135deg, #004d40, #00897b);
}
.confirm-dialog__header--graduated {
  background: linear-gradient(135deg, #311b92, #673ab7);
}
.confirm-dialog__header--inactive {
  background: linear-gradient(135deg, #e65100, #fb8c00);
}
.confirm-dialog__header--terminate {
  background: linear-gradient(135deg, #b71c1c, #e53935);
}
.confirm-dialog__title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
}
.confirm-dialog__sub {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 2px;
}
.confirm-info {
  font-size: 14px;
  color: #424242;
}
.confirm-list {
  background: #f9f9f9 !important;
  border-radius: 8px;
  border: 1px solid #eeeeee;
  max-height: 180px;
  overflow-y: auto;
}
</style>
